import * as React from "react";
import { useCallback, useMemo } from "react";
import {
  type MultiAccountAccountData,
  useMultiAccountSearchData,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import { isMultiAccountOrderSearchEnabled } from "~/utils/features";
import { useTranslation } from "react-i18next";
import { Typography } from "~/common/mui";
import { MultiLevelSelect, type MultiLevelSelectItem } from "~/common/components/MultiLevelSelect/MultiLevelSelect";
import { useTreeItemModel } from "@mui/x-tree-view-pro";
import { BaseTreeItem } from "~/common/components/MultiLevelSelect/BaseTreeItem";
import { groupByFn } from "~/common/utils/reducers";

// ────────────────────────────────────────────
// Data transformation
// ────────────────────────────────────────────

function transformAccounts(accounts: MultiAccountAccountData[]): MultiLevelSelectItem[] {
  return accounts.map((account) => ({
    id: `${account.code}--${account.salesOrg}`,
    label: account.name,
    subLabel: account.shortCode,
    children: account.endCustomer.map((ec) => ({
      id: `${account.code}-${ec.code}-${account.salesOrg}`,
      label: ec.name,
      subLabel: ec.code,
    })),
  }));
}

// ────────────────────────────────────────────
// Custom tree item (two-line label via BaseTreeItem)
// ────────────────────────────────────────────

const AccountTreeItem = React.forwardRef<HTMLLIElement, React.ComponentProps<typeof BaseTreeItem>>(
  function AccountTreeItem(props, ref) {
    const item = useTreeItemModel<MultiLevelSelectItem>(props.itemId);

    return (
      <BaseTreeItem
        {...props}
        ref={ref}
        renderContent={(label) => (
          <div>
            <Typography variant="bodyMedium">{label}</Typography>
            {item?.subLabel && (
              <Typography variant="bodySmall" color="text.secondary">
                {item.subLabel as string}
              </Typography>
            )}
          </div>
        )}
      />
    );
  },
);

// ────────────────────────────────────────────
// renderValue
// ────────────────────────────────────────────

function useRenderValue(accounts: MultiAccountAccountData[]) {
  return useCallback(
    (selectedIds: string[]) => {
      const parts = selectedIds.map((val) => val.split("-"));

      const grouped = Object.entries(
        groupByFn(
          parts,
          (item) => item[0],
          () => true,
        ),
      ).map(([accountCode, countingList]) => ({
        name: accounts.find((_) => _.code === accountCode)!.name,
        count: countingList.length,
      }));

      let maxLen = 13;
      if (grouped.length === 1) {
        maxLen = 99;
      } else if (grouped.length === 2) {
        maxLen = 20;
      }

      return grouped
        .map(({ name, count }) => {
          const truncated = name.length > maxLen ? name.substring(0, maxLen - 3) + "..." : name;
          return `${truncated} (${count})`;
        })
        .join(", ");
    },
    [accounts],
  );
}

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────

interface Props {
  onChange?: (newValues: string[]) => void;
  className?: string;
  values: string[];
}

export const MultiAccountSelect = ({ values, onChange, className }: Props) => {
  const { accounts } = useMultiAccountSearchData();
  const { t } = useTranslation();

  const items = useMemo(() => transformAccounts(accounts), [accounts]);
  const renderValue = useRenderValue(accounts);

  const handleChange = useCallback(
    (newValues: string[]) => {
      if (!onChange) {
        return;
      }
      if (newValues.length === 0) {
        onChange(getInitialAccounts());
      } else {
        onChange(newValues);
      }
    },
    [onChange],
  );

  return (
    <MultiLevelSelect
      className={className}
      multiple
      label={t("orderListPage.search.multiaccount")}
      items={items}
      value={values}
      onChange={handleChange}
      renderValue={renderValue}
      showSelectAll
      selectAllLabel={t("common.all")}
      fullWidth
      slots={{ item: AccountTreeItem }}
    />
  );
};

export function getInitialAccounts() {
  if (!isMultiAccountOrderSearchEnabled()) {
    return [];
  }

  const { uid, endCustomerCode, salesOrg } = window.SCHA.user.selectedAccount;

  if (endCustomerCode) {
    return [[uid, endCustomerCode, salesOrg].join("-")];
  } else {
    return [[uid, "", salesOrg].join("-")];
  }
}
